import { Product, type FiatPrice } from "@b2bportal/purchase-api";
import { OfferPaymentType, type WalletOffer } from "@hopper-b2b/types";

/**
 * Minimal shape of the BFF offer returned by GET /api/v0/wallet/offers.
 * Kept intentionally narrow so the app's full `Offer` type is assignable.
 */
export interface BffWalletOffer {
  id: string;
  name: string;
  expiresOn: string;
  trackingPropertiesV2: unknown;
  eligibleProducts?: string[];
  discount: {
    Discount: "Percentage" | "FiatAmount";
    percentage?: number;
    maxDiscount?: { fiat: FiatPrice };
    amount?: { fiat: FiatPrice };
  };
  offerContent?: {
    title?: string;
    lineItemDescription?: string;
    termsAndConditions?: string;
  };
}

/**
 * Returns true only for offers that are applicable to hotel bookings.
 * Filters out carrot cash credits and non-lodging offers (e.g. flight-only).
 */
export function isHotelOffer(offer: BffWalletOffer): boolean {
  if (!offer.eligibleProducts?.length) {
    // No eligibleProducts field — assume it's applicable (legacy offers)
    return true;
  }
  return offer.eligibleProducts.includes(Product.Hotel);
}

const DEFAULT_BRL_FIAT: FiatPrice = {
  currencyCode: "BRL",
  currencySymbol: "R$",
  value: 0,
};

export function mapBffOfferToWalletOffer(
  bffOffer: BffWalletOffer
): WalletOffer {
  const isPercentage = bffOffer.discount?.Discount === "Percentage";
  const fiatAmount: FiatPrice = isPercentage
    ? (bffOffer.discount.maxDiscount?.fiat ?? DEFAULT_BRL_FIAT)
    : (bffOffer.discount?.amount?.fiat ?? DEFAULT_BRL_FIAT);

  return {
    id: bffOffer.id,
    expiresOn: bffOffer.expiresOn,
    trackingPropertiesV2: bffOffer.trackingPropertiesV2,
    offerContent: {
      name: bffOffer.name,
      title: bffOffer.offerContent?.title,
      lineItemDescription: bffOffer.offerContent?.lineItemDescription,
      termsAndConditions: bffOffer.offerContent?.termsAndConditions,
    },
    percentage: isPercentage ? bffOffer.discount.percentage : undefined,
    maxDiscountAmount: isPercentage
      ? bffOffer.discount.maxDiscount?.fiat
      : undefined,
    amount: fiatAmount,
    payment: {
      value: {
        offerId: bffOffer.id,
        totalDiscountAmount: {
          fiat: fiatAmount,
          accountSpecific: {},
        },
        paymentType: OfferPaymentType.Offer,
      },
    },
  };
}
